import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin";
import { absoluteUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function FactoryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ plan_id: "", status: "active", days: 30 });

  const load = useCallback(() => {
    adminApi.get(`/factories/${id}`).then((r) => setData(r.data)).catch(() => nav("/admin/factories"));
  }, [id, nav]);

  useEffect(() => {
    load();
    adminApi.get("/plans").then((r) => setPlans(r.data));
  }, [load]);

  const openAssign = () => {
    setAssignForm({
      plan_id: data?.subscription?.plan_id || plans[0]?.id || "",
      status: data?.subscription?.status || "active",
      days: 30,
    });
    setAssignOpen(true);
  };

  const saveAssign = async () => {
    try {
      await adminApi.put(`/factories/${id}/subscription`, assignForm);
      toast.success("Subscription updated");
      setAssignOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const { user, designs, subscription, plan } = data;

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/admin/factories")} className="text-sm text-muted-foreground flex items-center gap-1">
        <ArrowLeft size={14} /> Back to factories
      </button>

      <div className="sq-card p-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="sq-label">Factory</div>
          <h1 className="text-3xl font-heading font-bold">{user.business_name || user.name || "—"}</h1>
          <div className="text-muted-foreground mt-1">{user.name} · {user.phone}</div>
          <div className="text-xs text-muted-foreground mt-1">Joined {user.created_at?.slice(0, 10)}</div>
        </div>
        <div className="text-right space-y-2">
          <div>
            <span className="sq-label">Plan</span>
            <div className="font-semibold">{plan?.name || "None"}</div>
            {subscription && (
              <div className="text-xs text-muted-foreground">
                {subscription.status} · expires {subscription.expires_at?.slice(0, 10)}
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={openAssign}>
            <CheckCircle2 size={14} className="mr-1" /> Assign plan
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-heading font-semibold text-xl">Orders ({designs.length})</h2>
        <Link to={`/admin/ai-insights?user=${id}`} className="text-sm text-primary hover:underline">
          View AI analyses →
        </Link>
      </div>

      <div className="grid gap-3">
        {designs.length === 0 && (
          <div className="sq-card p-8 text-center text-muted-foreground">No designs yet.</div>
        )}
        {designs.map((d) => (
          <div key={d.id} className="sq-card p-4 flex gap-4">
            <img src={absoluteUrl(d.image_url)} alt="" className="w-20 h-20 rounded-lg object-cover bg-muted" />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2">
                <div className="font-semibold truncate">{d.design_name}</div>
                <div className="font-heading font-bold text-primary shrink-0">
                  {inr(d.quote?.final_price)}
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {d.garment_type} · {d.fabric_type} · {Number(d.stitch_count).toLocaleString("en-IN")} stitches
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">{d.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign plan</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <div className="sq-label mb-1">Plan</div>
              <Select value={assignForm.plan_id} onValueChange={(v) => setAssignForm({ ...assignForm, plan_id: v })}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · ₹{p.price_monthly}/mo</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="sq-label mb-1">Status</div>
                <Select value={assignForm.status} onValueChange={(v) => setAssignForm({ ...assignForm, status: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="sq-label mb-1">Days</div>
                <Input type="number" className="h-11" value={assignForm.days}
                  onChange={(e) => setAssignForm({ ...assignForm, days: parseInt(e.target.value || "0", 10) })} />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={saveAssign}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
